import { ReactElement } from 'react';
import { useLocale } from '@hooks/useLocale';
import type { HitProps } from '@components/algolia/Hit';
import {
  BrandedStoreSliceType,
  BrandedStoreSlice,
} from '@adapters/prismic/types/brandedStorePage';
import FeaturedBestSellers from '../../BrandedStoreSlices/FeaturedBestSellers';
import Hero from '../../BrandedStoreSlices/Hero';
import ImageComponent from '../../BrandedStoreSlices/ImageComponent';
import PhotoGrid from '../../BrandedStoreSlices/PhotoGrid';
import BrandValues from '../../BrandedStoreSlices/BrandValues';
import CollectionAssortment from '../../BrandedStoreSlices/CollectionAssortment';
import FeaturedBox from '../../BrandedStoreSlices/FeaturedBox';
import { HomepageSlice } from '@adapters/prismic/types/homepage';
import IconGrid from '@components/cms/Slices/IconGrid';
import ImageBanner from '@components/cms/Slices/ImageBanner';
import CategoryNavigation from '@components/cms/Slices/CategoryNavigation';
import LinkTiles from '@components/cms/Slices/LinkTiles';
import BlogArticlesGrid from '@components/cms/BlogArticlesGrid';
import Container from '@components/global/Container';
import FormEmbed from '@components/cms/Slices/FormEmbed';
import { useTranslation } from 'next-i18next';
import {
  FormEmbedSlice,
  GenericSlice,
  HomepageValuePropSlice,
  PageType,
} from '@adapters/prismic/types/slices';
import HeroImage from '@components/cms/Slices/HeroImage';
import LinkCardGrid from '@components/cms/Slices/LinkCardGrid';
import CtaStack from '@components/cms/Slices/CtaStack';
import ProductGrid from '@components/cms/ProductGrid';
import PrimaryPromoBoxes from '@components/cms/Slices/PrimaryPromoBoxes';
import SecondaryPromoBoxes from '@components/cms/Slices/SecondaryPromoBoxes';
import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import CopyWithCta from '@components/cms/Slices/CopyWithCta';
import CategoryNavCarousel from '@components/cms/Slices/CategoryNavCarousel';
import HeroBanner from '@components/cms/Slices/HeroBanner';
import CategoryGrid from '@components/cms/Slices/CategoryGrid';
import HomepageValueProps from '@components/cms/BrandedStoreSlices/HomepageValueProps/HomepageValueProps';
import BestSellerRecommendations from '@components/cms/BrandedStoreSlices/BestSellerRecommendations';
import AlgonomyPlacementCarousel from '@components/cms/AlgonomyPlacementCarousel';
import SeoContentComponent from '@components/global/SeoContentComponent/SeoContentComponent';
import SeoContentAccordion from '_components/PLP/SeoContentAccordion';
import mapSeoAccordionData from '@adapters/prismic/utils/mapSeoAccordionData';
import {
  FEATURE_FLAG_SEO_ACCORDION,
  FEATURE_FLAG_MAIN_HERO,
  FEATURE_FLAG_LEARN_STRIP,
  FEATURE_FLAG_PROMO_TILES,
  FEATURE_FLAG_BRAND_TILES,
  FEATURE_FLAG_ACTIVITY_NAVIGATION_GRID,
  FEATURE_FLAG_IMAGE_HOTSPOT,
} from '@config/feature-flag';
import MainHero from '_components/MainHero';
import mapMainHeroData from '@adapters/prismic/utils/mapMainHeroData';
import LearnStrip from '_components/LearnStrip';
import mapLearnStripData from '@adapters/prismic/utils/mapLearnStripData';
import PromoTiles from '_components/PromoTiles';
import mapPromoTilesData from '@adapters/prismic/utils/mapPromoTilesData';
import BrandTiles from '_components/BrandTiles';
import mapBrandTilesData from '@adapters/prismic/utils/mapBrandTilesData';
import ActivityNavigationGrid from '_components/ActivityNavigationGrid';
import { transformActivityNavigationGrid } from '@adapters/prismic/transforms/activityNavigationGrid';
import ImageHotspot from '_components/ImageHotspot';
import { transformImageHotspot } from '@adapters/prismic/transforms/imageHotspot';
import { AlgonomyPlacement } from '@components/global/Recommendations/Loader';

import styles from '../LandingPageSliceZone.module.scss';

const LandingPageSliceHandler = ({
  slice,
  pageType,
  sliceIndex,
  recommendations,
  recommendationsCategory,
  imageHotspotProducts,
}: {
  slice:
    | HomepageSlice
    | BrandedStoreSlice
    | FormEmbedSlice
    | HomepageValuePropSlice
    | GenericSlice;
  pageType: PageType;
  sliceIndex: number;
  recommendations: AlgonomyPlacement[] | null;
  recommendationsCategory?: string | null;
  /** ED-345: SKU->product data resolved in getStaticProps for image_hotspot slices. */
  imageHotspotProducts?: Record<string, HitProps>;
}): ReactElement | null => {
  const locale = useLocale();
  const { t } = useTranslation('common');

  const bestSellersRecommendations = recommendations?.filter(
    (recommendation) => recommendation.name === 'category_page.landing_page_rr'
  );

  const manualRecommendations = recommendations?.filter(
    (recommendation) => recommendation.name === 'category_page.landing_page1_rr'
  );

  const collectionAssortmentRecommendations1 = recommendations?.filter(
    (recommendation) => recommendation.name === 'category_page.landing_page2_rr'
  );

  const collectionAssortmentRecommendations2 = recommendations?.filter(
    (recommendation) => recommendation.name === 'category_page.landing_page3_rr'
  );

  switch (slice?.slice_type) {
    case 'hero_banner':
      return (
        <HeroBanner
          mediaType={slice.primary.media_type}
          mobileImage={slice.primary.mobile_image}
          desktopImage={slice.primary.desktop_image}
          mobileVideo={slice.primary.mobile_video}
          desktopVideo={slice.primary.desktop_video}
          videoLabel={slice.primary.video_alt_text}
          url={slice.primary.url}
          blackText={slice.primary.black_text}
          redText={slice.primary.red_text}
          redTextPlacement={slice.primary.red_text_placement}
          redTextNewLine={slice.primary.red_text_new_line}
          bodyCopy={slice.primary.body_copy}
          ctaText={slice.primary.cta_text}
          ctaType={slice.primary.cta_type}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.mainHero:
      return FEATURE_FLAG_MAIN_HERO ? (
        <MainHero data={mapMainHeroData(slice.primary)} />
      ) : null;
    case BrandedStoreSliceType.learnStrip:
      return FEATURE_FLAG_LEARN_STRIP ? (
        <LearnStrip {...mapLearnStripData(slice.primary, slice.items)} />
      ) : null;
    case BrandedStoreSliceType.promoTiles:
      return FEATURE_FLAG_PROMO_TILES ? (
        <PromoTiles {...mapPromoTilesData(slice.primary, slice.items)} />
      ) : null;
    case BrandedStoreSliceType.brandTiles:
      return FEATURE_FLAG_BRAND_TILES ? (
        <BrandTiles
          {...mapBrandTilesData(slice.primary, slice.items)}
          priority={sliceIndex === 0}
        />
      ) : null;
    case BrandedStoreSliceType.activityNavigationGrid: {
      if (!FEATURE_FLAG_ACTIVITY_NAVIGATION_GRID) {
        return null;
      }
      const activityNavigationGridData = transformActivityNavigationGrid(
        slice,
        locale
      );
      return activityNavigationGridData ? (
        <ActivityNavigationGrid
          heading={activityNavigationGridData.heading}
          body={activityNavigationGridData.body}
          items={activityNavigationGridData.items}
        />
      ) : null;
    }
    case 'image_hotspot': {
      if (!FEATURE_FLAG_IMAGE_HOTSPOT) {
        return null;
      }
      const imageHotspotData = transformImageHotspot(
        slice,
        imageHotspotProducts
      );
      return imageHotspotData ? (
        <ImageHotspot
          tabs={imageHotspotData.tabs}
          heading={imageHotspotData.heading}
          priority={sliceIndex === 0}
        />
      ) : null;
    }
    case 'hero_image':
      return (
        <HeroImage
          image={slice.primary.image}
          link={slice.primary.link}
          heading={slice.primary.heading}
          content={slice.primary.content}
          button_text={slice.primary.button_text}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'primary_promo_boxes':
      return (
        <PrimaryPromoBoxes
          title={slice.primary.title}
          centerTitle={slice.primary?.center_align}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'secondary_promo_boxes':
      return (
        <SecondaryPromoBoxes
          title={slice.primary.title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'category_nav_carousel':
      return (
        <CategoryNavCarousel
          title={slice.primary.title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'featured_brands':
      return (
        <IconGrid
          title={slice.primary.section_title}
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'image_banner':
      return (
        <ImageBanner
          image={slice.primary.image}
          title={slice.primary.title}
          content={slice.primary.content}
          text_alignment={slice.primary.text_alignment}
          button_text={slice.primary.button_text}
          button_link={slice.primary.button_link}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'link_card_grid':
      return (
        <LinkCardGrid
          items={slice?.items}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'category_navigation':
      return (
        <CategoryNavigation
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'link_tiles':
      return (
        <LinkTiles
          items={slice.items}
          trackPromos
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      );
    case 'featured_blog_posts':
      return (
        <Container>
          <BlogArticlesGrid
            blogPosts={slice.blog_posts}
            title={slice?.primary?.heading}
            linkTitle={t('recent.articles.button')}
          />
        </Container>
      );
    case 'formembed':
      return <FormEmbed html={slice.primary.embedcode} />;
    case BrandedStoreSliceType.hero:
      return (
        <Hero
          locale={locale}
          primary={slice?.primary}
          sliceIndex={sliceIndex}
        />
      );
    case BrandedStoreSliceType.collectionAssortment:
      return (
        <CollectionAssortment
          locale={locale}
          primary={slice?.primary}
          items={slice?.items}
          products={
            slice?.primary?.collection_path ===
            'category_page.collection_assortment1_rr'
              ? collectionAssortmentRecommendations1?.[0]?.items
              : collectionAssortmentRecommendations2?.[0]?.items
          }
        />
      );
    case BrandedStoreSliceType.photoGrid:
      return (
        <PhotoGrid
          locale={locale}
          items={slice?.items}
          primary={slice?.primary}
        />
      );
    case BrandedStoreSliceType.brandValues:
      return <BrandValues primary={slice?.primary} items={slice?.items} />;
    case BrandedStoreSliceType.valueProps:
      return (
        <HomepageValueProps
          primary={slice?.primary}
          items={'items' in slice ? slice?.items : []}
        />
      );
    case BrandedStoreSliceType.featuredBox:
      return <FeaturedBox primary={slice?.primary} locale={locale} />;
    case 'cta_stack':
      return (
        <CtaStack
          primary={slice?.primary}
          items={slice?.items}
          locale={locale}
        />
      );
    case BrandedStoreSliceType.featuredBestSellers:
      return <FeaturedBestSellers primary={slice?.primary} locale={locale} />;
    case BrandedStoreSliceType.imageComponent:
      return <ImageComponent primary={slice?.primary} items={slice?.items} />;
    case BrandedStoreSliceType.productGridWithTooltips:
      return (
        <ProductGrid
          title={slice.primary.title}
          id={slice?.primary?.id}
          body={slice?.primary?.copy?.[0]?.text}
          images={slice?.items}
          cta={{
            text: slice.primary.cta_text,
            url: slice.primary.cta_url,
            importance: slice.primary.cta_importance,
          }}
          altPosition={slice.primary?.alt_position}
          icon={slice.primary?.icon}
          socialProof={{
            top: slice?.primary?.social_proof_top?.[0]?.text ?? '',
            bottom: slice?.primary?.social_proof_bottom?.[0]?.text ?? '',
            topSize: slice.primary.social_proof_top_size,
            bottomSize: slice.primary.social_proof_bottom_size,
          }}
        />
      );
    case BrandedStoreSliceType.markdownCopy:
      return (
        <MarkdownCopy
          text={slice?.primary?.copy?.[0]?.text}
          className={styles.markdown}
        />
      );
    case BrandedStoreSliceType.copyWithCta:
      return <CopyWithCta primary={slice?.primary} />;
    case 'category_grid':
      return (
        <CategoryGrid
          title={slice?.primary?.title}
          items={slice?.items}
          mobileScroll={slice?.primary?.mobile_scroll}
        />
      );
    case 'recommendations':
      return (
        <BestSellerRecommendations
          featuredBestSellersRecommendations={
            recommendationsCategory === 'manual'
              ? manualRecommendations
              : bestSellersRecommendations
          }
        />
      );
    case 'algonomy_placement':
      return (
        <AlgonomyPlacementCarousel
          placement={recommendations?.find(
            (recommendation) =>
              recommendation.name === slice.primary?.placement_name
          )}
          coOpBrand={slice.primary?.co_op_brand}
        />
      );
    case BrandedStoreSliceType.seoContentComponent:
      return FEATURE_FLAG_SEO_ACCORDION ? (
        <SeoContentAccordion data={mapSeoAccordionData(slice?.primary)} />
      ) : (
        <SeoContentComponent data={slice?.primary} />
      );
    default:
      return null;
  }
};

export default LandingPageSliceHandler;
